import { useState, useEffect, useCallback, useRef } from "react";
import { StoryboardModal } from "../storyboard/StoryboardModal";
import { CharacterManagerModal } from "./CharacterManagerModal";

type Scene = {
  id: string;
  sceneCode: string;
  location: string | null;
  title: string;
  description: string | null;
  productionNotes: string | null;
  status: "draft" | "approved" | "killed" | "shot";
  timelineDate: string | null;
  timelineDateEnd: string | null;
  linkedEventId: string | null;
  linkedEraId: string | null;
  images: SceneImage[];
  characters: SceneCharacter[];
  linkedEvent: { id: string; title: string; date: string } | null;
  linkedEra: { id: string; title: string; startDate: string; endDate: string } | null;
};

type SceneImage = {
  id: string;
  imageUrl: string;
  sortOrder: number;
  caption: string | null;
};

type SceneCharacter = {
  personId: string;
  name: string;
  imageUrl: string | null;
};

type SceneOptions = {
  locations: string[];
  codes: string[];
  statuses: string[];
  events: { id: string; title: string; date: string }[];
  eras: { id: string; title: string; startDate: string }[];
  people: { id: string; name: string; imageUrl: string | null }[];
};

type Props = {
  sceneId: string;
  x: number;
  y: number;
  canEdit: boolean;
  canEditCharacters?: boolean;
  canEditStoryboard?: boolean;
  onClose: () => void;
  onSceneUpdated: () => void;
};

type TimelineLinkType = "none" | "date" | "range" | "event" | "era";

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  draft: { bg: "bg-gray-500/20", border: "border-gray-500/40", text: "text-gray-400" },
  approved: { bg: "bg-teal-500/20", border: "border-teal-500/40", text: "text-teal-400" },
  killed: { bg: "bg-red-500/20", border: "border-red-500/40", text: "text-red-400" },
  shot: { bg: "bg-green-500/20", border: "border-green-500/40", text: "text-green-400" },
};

// Normal popup dimensions (increased)
const POPUP_WIDTH = 720;
const POPUP_MAX_HEIGHT = 720;

// Expanded popup dimensions (increased)
const EXPANDED_WIDTH = 1000;
const EXPANDED_HEIGHT = 800;
const EXPANDED_MARGIN = 60;

const MIN_FRAME_SLOTS = 3; // Minimum slots to show when empty

export function SceneDetailPopup({ sceneId, x, y, canEdit, canEditCharacters, canEditStoryboard, onClose, onSceneUpdated }: Props) {
  // Resolve scoped permissions - fall back to canEdit if not provided
  const effectiveCanEditCharacters = canEditCharacters ?? canEdit;
  const effectiveCanEditStoryboard = canEditStoryboard ?? canEdit;
  const popupRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [scene, setScene] = useState<Scene | null>(null);
  const [options, setOptions] = useState<SceneOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [productionNotes, setProductionNotes] = useState("");
  const [status, setStatus] = useState<string>("draft");
  const [activeTab, setActiveTab] = useState<"description" | "prodNotes">("description");

  // Timeline linking state
  const [timelineLinkType, setTimelineLinkType] = useState<TimelineLinkType>("none");
  const [timelineDate, setTimelineDate] = useState("");
  const [timelineDateEnd, setTimelineDateEnd] = useState("");
  const [linkedEventId, setLinkedEventId] = useState("");
  const [linkedEraId, setLinkedEraId] = useState("");

  // Character search
  const [characterSearch, setCharacterSearch] = useState("");
  const [showCharacterDropdown, setShowCharacterDropdown] = useState(false);

  // Image upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isImageDragging, setIsImageDragging] = useState(false);

  // Storyboard modal state
  const [storyboardOpen, setStoryboardOpen] = useState(false);

  // Character manager modal state
  const [characterManagerPersonId, setCharacterManagerPersonId] = useState<string | null>(null);

  // Dragging state for popup
  const [isPopupDragging, setIsPopupDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Calculate popup position with edge detection
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (isExpanded) {
      // Expanded view: position in upper right with margin
      setPosition({
        left: window.innerWidth - EXPANDED_WIDTH - EXPANDED_MARGIN,
        top: EXPANDED_MARGIN,
      });
    } else {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Default to bottom-center of viewport
      let left = (viewportWidth - POPUP_WIDTH) / 2;
      let top = viewportHeight - POPUP_MAX_HEIGHT - 40;

      // Edge detection
      if (left + POPUP_WIDTH > viewportWidth - 20) {
        left = viewportWidth - POPUP_WIDTH - 20;
      }
      if (left < 20) {
        left = 20;
      }
      if (top + POPUP_MAX_HEIGHT > viewportHeight - 20) {
        top = viewportHeight - POPUP_MAX_HEIGHT - 20;
      }
      if (top < 20) {
        top = 20;
      }

      setPosition({ left, top });
    }
  }, [isExpanded]);

  // Drag handlers for popup
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Only drag from the header area
    if ((e.target as HTMLElement).closest('button, select, input')) return;

    setIsPopupDragging(true);
    setDragOffset({
      x: e.clientX - position.left,
      y: e.clientY - position.top,
    });
  }, [position]);

  useEffect(() => {
    if (!isPopupDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const currentWidth = isExpanded ? EXPANDED_WIDTH : POPUP_WIDTH;
      const currentMaxHeight = isExpanded ? EXPANDED_HEIGHT : POPUP_MAX_HEIGHT;

      let newLeft = e.clientX - dragOffset.x;
      let newTop = e.clientY - dragOffset.y;

      // Keep popup within viewport bounds
      newLeft = Math.max(20, Math.min(viewportWidth - currentWidth - 20, newLeft));
      newTop = Math.max(20, Math.min(viewportHeight - currentMaxHeight - 20, newTop));

      setPosition({
        left: newLeft,
        top: newTop,
      });
    };

    const handleMouseUp = () => setIsPopupDragging(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPopupDragging, dragOffset, isExpanded]);

  // Load scene data and options
  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const [sceneRes, optionsRes] = await Promise.all([
          fetch(`/api/scenes/${sceneId}`, { credentials: "include" }),
          fetch("/api/scenes/options", { credentials: "include" }),
        ]);

        if (!sceneRes.ok) throw new Error("Failed to load scene");

        const sceneData = await sceneRes.json();
        setScene(sceneData.scene);
        populateForm(sceneData.scene);

        if (optionsRes.ok) {
          const optionsData = await optionsRes.json();
          setOptions(optionsData);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load scene");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [sceneId]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const populateForm = (s: Scene) => {
    setTitle(s.title);
    setLocation(s.location || "");
    setDescription(s.description || "");
    setProductionNotes(s.productionNotes || "");
    setStatus(s.status);

    // Detect timeline link type
    if (s.linkedEventId) {
      setTimelineLinkType("event");
      setLinkedEventId(s.linkedEventId);
      setTimelineDate("");
      setTimelineDateEnd("");
      setLinkedEraId("");
    } else if (s.linkedEraId) {
      setTimelineLinkType("era");
      setLinkedEraId(s.linkedEraId);
      setTimelineDate("");
      setTimelineDateEnd("");
      setLinkedEventId("");
    } else if (s.timelineDate && s.timelineDateEnd) {
      setTimelineLinkType("range");
      setTimelineDate(s.timelineDate);
      setTimelineDateEnd(s.timelineDateEnd);
      setLinkedEventId("");
      setLinkedEraId("");
    } else if (s.timelineDate) {
      setTimelineLinkType("date");
      setTimelineDate(s.timelineDate);
      setTimelineDateEnd("");
      setLinkedEventId("");
      setLinkedEraId("");
    } else {
      setTimelineLinkType("none");
      setTimelineDate("");
      setTimelineDateEnd("");
      setLinkedEventId("");
      setLinkedEraId("");
    }
  };

  const getSceneCodeFromLocation = (loc: string): string => {
    const match = loc.match(/^(L\d+)/);
    return match ? match[1] : "L1";
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    setError(null);

    try {
      const sceneCode = location ? getSceneCodeFromLocation(location) : scene?.sceneCode || "L1";

      // Build payload based on link type
      const payload: Record<string, unknown> = {
        sceneCode,
        title,
        location: location || null,
        description: description || null,
        productionNotes: productionNotes || null,
        status,
        timelineDate: null,
        timelineDateEnd: null,
        linkedEventId: null,
        linkedEraId: null,
      };

      switch (timelineLinkType) {
        case "date":
          payload.timelineDate = timelineDate || null;
          break;
        case "range":
          payload.timelineDate = timelineDate || null;
          payload.timelineDateEnd = timelineDateEnd || null;
          break;
        case "event":
          payload.linkedEventId = linkedEventId || null;
          break;
        case "era":
          payload.linkedEraId = linkedEraId || null;
          break;
        case "none":
        default:
          // All link fields stay null
          break;
      }

      const res = await fetch(`/api/scenes/${sceneId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save");
      }

      // Refetch the scene to show updated data, but keep the popup open
      const sceneRes = await fetch(`/api/scenes/${sceneId}`, { credentials: "include" });
      if (sceneRes.ok) {
        const data = await sceneRes.json();
        setScene(data.scene);
      }

      // Notify parent of update (for timeline refresh) but don't close
      onSceneUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit) return;
    if (!confirm("Are you sure you want to delete this scene?")) return;

    try {
      const res = await fetch(`/api/scenes/${sceneId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete");

      onSceneUpdated();
      onClose();
    } catch (e) {
      setError("Failed to delete scene");
    }
  };

  const handleAddCharacter = async (personId: string) => {
    if (!canEdit) return;

    try {
      const res = await fetch(`/api/scenes/${sceneId}/characters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ personId }),
      });

      if (res.ok) {
        const sceneRes = await fetch(`/api/scenes/${sceneId}`, { credentials: "include" });
        if (sceneRes.ok) {
          const data = await sceneRes.json();
          setScene(data.scene);
        }
      }
    } catch (e) {
      setError("Failed to add character");
    }
    setShowCharacterDropdown(false);
    setCharacterSearch("");
  };

  const handleRemoveCharacter = async (personId: string) => {
    if (!canEdit) return;

    try {
      await fetch(`/api/scenes/${sceneId}/characters/${personId}`, {
        method: "DELETE",
        credentials: "include",
      });

      setScene((prev) =>
        prev ? { ...prev, characters: prev.characters.filter((c) => c.personId !== personId) } : null
      );
    } catch (e) {
      setError("Failed to remove character");
    }
  };

  const filteredPeople = options?.people.filter(
    (p) =>
      p.name.toLowerCase().includes(characterSearch.toLowerCase()) &&
      !scene?.characters.some((c) => c.personId === p.id)
  );

  const handleImageUpload = useCallback(async (files: FileList | File[]) => {
    if (!canEdit || files.length === 0) return;

    setIsUploading(true);
    setError(null);
    const filesArray = Array.from(files);
    setUploadProgress({ current: 0, total: filesArray.length });

    try {
      for (let i = 0; i < filesArray.length; i++) {
        setUploadProgress({ current: i + 1, total: filesArray.length });
        const file = filesArray[i];
        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`/api/scenes/${sceneId}/images/upload`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to upload image");
        }
      }

      const sceneRes = await fetch(`/api/scenes/${sceneId}`, { credentials: "include" });
      if (sceneRes.ok) {
        const data = await sceneRes.json();
        setScene(data.scene);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to upload image");
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  }, [canEdit, sceneId]);

  const handleDeleteImage = useCallback(async (imageId: string) => {
    if (!canEdit) return;
    if (!confirm("Delete this image?")) return;

    try {
      const res = await fetch(`/api/scenes/${sceneId}/images/${imageId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to delete image");

      setScene((prev) =>
        prev ? { ...prev, images: prev.images.filter((img) => img.id !== imageId) } : null
      );
    } catch (e) {
      setError("Failed to delete image");
    }
  }, [canEdit, sceneId]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (canEdit) {
      setIsImageDragging(true);
    }
  }, [canEdit]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsImageDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsImageDragging(false);

    if (!canEdit) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const imageFiles = Array.from(files).filter((f) =>
        ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(f.type)
      );
      if (imageFiles.length > 0) {
        handleImageUpload(imageFiles);
      }
    }
  }, [canEdit, handleImageUpload]);

  const statusColors = STATUS_COLORS[status] || STATUS_COLORS.draft;
  const currentWidth = isExpanded ? EXPANDED_WIDTH : POPUP_WIDTH;
  const currentMaxHeight = isExpanded ? EXPANDED_HEIGHT : POPUP_MAX_HEIGHT;

  // Frame sizes based on expanded state (increased)
  const frameWidth = isExpanded ? 140 : 100;
  const frameHeight = isExpanded ? 95 : 70;

  if (isLoading) {
    return (
      <div
        ref={popupRef}
        className="fixed z-50 bg-surface border border-border rounded-lg shadow-2xl p-6"
        style={{ left: position.left, top: position.top, width: currentWidth }}
      >
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!scene) {
    return (
      <div
        ref={popupRef}
        className="fixed z-50 bg-surface border border-border rounded-lg shadow-2xl p-6"
        style={{ left: position.left, top: position.top, width: currentWidth }}
      >
        <p className="text-red-400 text-sm">Scene not found</p>
      </div>
    );
  }

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-surface border border-border rounded-lg shadow-2xl overflow-hidden"
      style={{ left: position.left, top: position.top, width: currentWidth, maxHeight: currentMaxHeight }}
    >
      {/* Header - draggable */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${statusColors.border} ${statusColors.bg} ${isPopupDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {canEdit ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Scene title..."
              className="flex-1 bg-transparent border-none text-foreground font-copernicus text-lg placeholder:text-muted focus:outline-none focus:ring-0"
            />
          ) : (
            <span className="text-foreground font-copernicus text-lg truncate">{title}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className={`px-2 py-1 rounded text-xs font-medium ${statusColors.bg} ${statusColors.text} border ${statusColors.border} bg-transparent focus:outline-none cursor-pointer`}
            >
              {options?.statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isExpanded ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9L4 4m0 0v5m0-5h5m11 11l-5 5m5 0v-5m0 5h-5" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              )}
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 text-muted hover:text-foreground hover:bg-border rounded transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-3 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-xs">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: currentMaxHeight - 140 }}>
        {/* Location Row */}
        <div>
          <label className="block text-[10px] text-muted mb-1.5 uppercase tracking-wider">Location</label>
          {canEdit ? (
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
            >
              <option value="">Select location...</option>
              {options?.locations.map((loc) => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-foreground">{location || "No location"}</span>
          )}
        </div>

        {/* Timeline Link Section */}
        <div>
          <label className="block text-[10px] text-muted mb-1.5 uppercase tracking-wider">Timeline Link</label>
          {canEdit ? (
            <div className="space-y-3">
              {/* Link type radio buttons */}
              <div className="flex flex-wrap gap-3">
                {[
                  { value: "none", label: "None" },
                  { value: "date", label: "Date" },
                  { value: "range", label: "Date Range" },
                  { value: "event", label: "Event" },
                  { value: "era", label: "Era" },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="timelineLinkType"
                      value={value}
                      checked={timelineLinkType === value}
                      onChange={() => setTimelineLinkType(value as TimelineLinkType)}
                      className="w-3.5 h-3.5 accent-primary-500"
                    />
                    <span className="text-sm text-foreground">{label}</span>
                  </label>
                ))}
              </div>

              {/* Conditional inputs based on link type */}
              {timelineLinkType === "date" && (
                <input
                  type="date"
                  value={timelineDate}
                  onChange={(e) => setTimelineDate(e.target.value)}
                  className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
                />
              )}

              {timelineLinkType === "range" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] text-muted mb-1 uppercase tracking-wider">Start</label>
                    <input
                      type="date"
                      value={timelineDate}
                      onChange={(e) => setTimelineDate(e.target.value)}
                      className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-muted mb-1 uppercase tracking-wider">End</label>
                    <input
                      type="date"
                      value={timelineDateEnd}
                      onChange={(e) => setTimelineDateEnd(e.target.value)}
                      className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
                    />
                  </div>
                </div>
              )}

              {timelineLinkType === "event" && (
                <select
                  value={linkedEventId}
                  onChange={(e) => setLinkedEventId(e.target.value)}
                  className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
                >
                  <option value="">Select event...</option>
                  {options?.events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title} ({new Date(event.date).getFullYear()})
                    </option>
                  ))}
                </select>
              )}

              {timelineLinkType === "era" && (
                <select
                  value={linkedEraId}
                  onChange={(e) => setLinkedEraId(e.target.value)}
                  className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground"
                >
                  <option value="">Select era...</option>
                  {options?.eras.map((era) => (
                    <option key={era.id} value={era.id}>
                      {era.title} ({new Date(era.startDate).getFullYear()})
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <span className="text-sm text-foreground">
              {scene.linkedEvent
                ? `Event: ${scene.linkedEvent.title}`
                : scene.linkedEra
                ? `Era: ${scene.linkedEra.title}`
                : scene.timelineDate && scene.timelineDateEnd
                ? `${scene.timelineDate} - ${scene.timelineDateEnd}`
                : scene.timelineDate
                ? scene.timelineDate
                : "Not linked"}
            </span>
          )}
        </div>

        {/* Storyboard & Description Row */}
        <div className={`flex gap-5 ${isExpanded ? "flex-row" : "flex-col sm:flex-row"}`}>
          {/* Storyboard Frames */}
          <div
            className={isExpanded ? "w-1/2" : "flex-shrink-0"}
            onDragOver={effectiveCanEditStoryboard ? handleDragOver : undefined}
            onDragLeave={effectiveCanEditStoryboard ? handleDragLeave : undefined}
            onDrop={effectiveCanEditStoryboard ? handleDrop : undefined}
          >
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10px] text-muted uppercase tracking-wider">Storyboard</label>
              <button
                onClick={() => setStoryboardOpen(true)}
                className="text-[10px] text-primary-400 hover:text-primary-300 transition-colors"
              >
                Open Full View
              </button>
            </div>
            <div
              className={`grid gap-2 p-3 rounded-lg border-2 border-dashed transition-colors max-h-[200px] overflow-y-auto ${isImageDragging ? "border-primary-500 bg-primary-500/10" : "border-border bg-surface-elevated/50"}`}
              style={{ gridTemplateColumns: `repeat(${isExpanded ? 4 : 3}, minmax(0, 1fr))` }}
            >
              {/* Render all existing images */}
              {scene.images.map((image, idx) => (
                <div
                  key={image.id}
                  className="relative bg-background rounded overflow-hidden group"
                  style={{ width: frameWidth, height: frameHeight }}
                >
                  <img
                    src={image.imageUrl}
                    alt={image.caption || `Frame ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {effectiveCanEditStoryboard && (
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {/* Add button - always show if canEditStoryboard */}
              {effectiveCanEditStoryboard && (
                <div
                  className="relative bg-background rounded overflow-hidden"
                  style={{ width: frameWidth, height: frameHeight }}
                >
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-full flex items-center justify-center text-muted hover:text-foreground hover:bg-border transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              )}
              {/* Show placeholder slots if no images and not editing */}
              {!effectiveCanEditStoryboard && scene.images.length === 0 && Array.from({ length: MIN_FRAME_SLOTS }).map((_, idx) => (
                <div
                  key={`empty-${idx}`}
                  className="relative bg-background rounded overflow-hidden"
                  style={{ width: frameWidth, height: frameHeight }}
                >
                  <div className="w-full h-full flex items-center justify-center text-muted opacity-30">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              ))}
            </div>
            {isUploading && (
              <div className="mt-2 text-xs text-primary-400 flex items-center gap-2">
                <div className="w-3 h-3 border border-primary-500 border-t-transparent rounded-full animate-spin" />
                <span>
                  {uploadProgress.total > 1
                    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                    : "Uploading..."}
                </span>
                {uploadProgress.total > 1 && (
                  <div className="flex-1 h-1 bg-border rounded-full overflow-hidden max-w-[100px]">
                    <div
                      className="h-full bg-primary-500 transition-all duration-200"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={(e) => {
                if (e.target.files) {
                  handleImageUpload(e.target.files);
                  e.target.value = "";
                }
              }}
              className="hidden"
            />
          </div>

          {/* Description / Production Notes */}
          <div className={isExpanded ? "w-1/2" : "flex-1 min-w-0"}>
            <div className="flex items-center gap-4 mb-1.5">
              <button
                onClick={() => setActiveTab("description")}
                className={`text-[10px] uppercase tracking-wider font-medium transition-colors ${
                  activeTab === "description" ? "text-primary-400" : "text-muted hover:text-muted-foreground"
                }`}
              >
                Description
              </button>
              <button
                onClick={() => setActiveTab("prodNotes")}
                className={`text-[10px] uppercase tracking-wider font-medium transition-colors ${
                  activeTab === "prodNotes" ? "text-primary-400" : "text-muted hover:text-muted-foreground"
                }`}
              >
                Prod. Notes
              </button>
            </div>
            {canEdit ? (
              <textarea
                value={activeTab === "description" ? description : productionNotes}
                onChange={(e) => activeTab === "description" ? setDescription(e.target.value) : setProductionNotes(e.target.value)}
                placeholder={activeTab === "description" ? "Scene description..." : "Production notes..."}
                className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted resize-none"
                style={{ minHeight: isExpanded ? 240 : 180 }}
              />
            ) : (
              <p className="text-sm text-foreground/80 whitespace-pre-wrap p-3 bg-surface-elevated rounded border border-border" style={{ minHeight: isExpanded ? 240 : 180 }}>
                {activeTab === "description"
                  ? (description || "No description")
                  : (productionNotes || "No production notes")}
              </p>
            )}
          </div>
        </div>

        {/* Characters */}
        <div className={showCharacterDropdown && filteredPeople && filteredPeople.length > 0 ? "pb-44" : ""}>
          <label className="block text-[10px] text-muted mb-1.5 uppercase tracking-wider">Characters</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {scene.characters.map((char) => (
              <button
                key={char.personId}
                onClick={() => setCharacterManagerPersonId(char.personId)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-elevated border border-border rounded-full text-sm hover:border-primary-500/40 hover:bg-primary-500/5 transition-colors cursor-pointer"
              >
                {char.imageUrl && (
                  <img src={char.imageUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                )}
                <span className="text-foreground">{char.name}</span>
                {effectiveCanEditCharacters && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveCharacter(char.personId);
                    }}
                    className="text-muted hover:text-red-400 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </span>
                )}
              </button>
            ))}
            {scene.characters.length === 0 && (
              <span className="text-xs text-muted italic">No characters tagged</span>
            )}
          </div>
          {effectiveCanEditCharacters && (
            <div className="relative">
              <input
                type="text"
                value={characterSearch}
                onChange={(e) => {
                  setCharacterSearch(e.target.value);
                  setShowCharacterDropdown(true);
                }}
                onFocus={() => setShowCharacterDropdown(true)}
                placeholder="+ Add character..."
                className="w-full bg-surface-elevated border border-border rounded px-3 py-2 text-sm text-foreground placeholder:text-muted"
              />
              {showCharacterDropdown && filteredPeople && filteredPeople.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-surface border border-border rounded-lg shadow-lg max-h-44 overflow-y-auto z-10">
                  {filteredPeople.slice(0, 8).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleAddCharacter(p.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-surface-elevated"
                    >
                      {p.imageUrl && (
                        <img src={p.imageUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                      )}
                      <span className="text-foreground">{p.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {canEdit && (
        <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-2 bg-surface-elevated">
          <button
            onClick={handleDelete}
            className="px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
          >
            Delete
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-muted hover:text-foreground hover:bg-border rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !title}
              className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Storyboard Modal */}
      {storyboardOpen && (
        <StoryboardModal
          sceneId={sceneId}
          sceneName={title}
          isOpen={storyboardOpen}
          onClose={() => {
            setStoryboardOpen(false);
            // Refetch scene to get updated storyboard data
            fetch(`/api/scenes/${sceneId}`, { credentials: "include" })
              .then((res) => res.json())
              .then((data) => setScene(data.scene))
              .catch(console.error);
          }}
          canEdit={effectiveCanEditStoryboard}
        />
      )}

      {/* Character Manager Modal */}
      {characterManagerPersonId && (
        <CharacterManagerModal
          personId={characterManagerPersonId}
          canEdit={effectiveCanEditCharacters}
          onClose={() => setCharacterManagerPersonId(null)}
        />
      )}
    </div>
  );
}
